import React from 'react';

import { GtpWalletInfoResult } from '@eika-infrastruktur/mobilbank-lib';
import { ErrorMessage } from '@eika/attention-message';
import { IconTypes } from '@eika/icon';

import { Logger } from '@/domain/app';
import { CreditCardService, useCreditCardDetails } from '@/domain/card';
import { useEligibleCreditCardsQueryForGoogle, useGoogleWalletInformation } from '@/domain/digital-wallet';

import { CouldNotFetchCardsErrorCard } from '@components/error/ErrorCard';
import { CreditCard } from '@generated-types';
import useText from '@hooks/use-text';

import { GetGoingContent } from './GetGoingContent';
import { GetGoingButtonSkeleton } from './GetGoingSkeleton';

type V4DigitalWalletQueryProps = {
    card: CreditCard;
    googleWalletInfo: GtpWalletInfoResult;
};

const V4DigitalWalletQuery = ({ card, googleWalletInfo }: V4DigitalWalletQueryProps) => {
    const { googleWalletNotAvailable } = useText();

    const eligibleCardsQuery = useEligibleCreditCardsQueryForGoogle(googleWalletInfo, {
        select: (data) => data.find((eCard) => eCard.id === card.id),
    });

    if (eligibleCardsQuery.isPending) {
        return <GetGoingButtonSkeleton />;
    }

    if (eligibleCardsQuery.isError || eligibleCardsQuery.data === undefined) {
        return <ErrorMessage text={googleWalletNotAvailable} />;
    }

    return <GetGoingContent data={{ card, elibigleCard: eligibleCardsQuery.data, source: 'kb' }} />;
};

export const GetGoingV4Query = () => {
    const cardQuery = useCreditCardDetails();

    const googleWalletInfoQuery = useGoogleWalletInformation();
    const { googleWalletNotCardOwnerError, googleWalletGetGoingFetchError } = useText();

    if (cardQuery.isPending || googleWalletInfoQuery.isPending) {
        return <GetGoingButtonSkeleton />;
    }

    if (cardQuery.isError) {
        return <CouldNotFetchCardsErrorCard tryAgainClick={cardQuery.refetch} />;
    }

    if (googleWalletInfoQuery.isError) {
        return <ErrorMessage text={googleWalletGetGoingFetchError} icon={IconTypes.LUKK_24} />;
    }

    if (googleWalletInfoQuery.data.status === 'error') {
        return <ErrorMessage text={googleWalletInfoQuery.data.error} icon={IconTypes.LUKK_24} />;
    }

    const card = cardQuery.data;

    if (!CreditCardService.isOwnerOfCard(card)) {
        return <ErrorMessage text={googleWalletNotCardOwnerError} />;
    }

    Logger.info('wallet', 'google wallet info log', 'GetGoingV4Query', { data: googleWalletInfoQuery.data.data });

    return <V4DigitalWalletQuery card={card} googleWalletInfo={googleWalletInfoQuery.data.data} />;
};
